import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Stethoscope, Loader2, Save, Trash2, Plus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProcedureRow = ({ proc, onSaved, onDeleted }) => {
    const [name, setName] = useState(proc.name);
    const [price, setPrice] = useState(proc.price);
    const [description, setDescription] = useState(proc.description || "");
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirming, setConfirming] = useState(false);

    const save = async () => {
        const numPrice = parseInt(price, 10);
        if (!name.trim() || name.trim().length < 3) {
            toast.error("El nombre debe tener al menos 3 caracteres");
            return;
        }
        if (!numPrice || numPrice <= 0) {
            toast.error("Ingresa un precio válido");
            return;
        }
        setSaving(true);
        try {
            await axios.put(
                `${API}/admin/procedures/${proc.id}`,
                { name: name.trim(), price: numPrice, description: description.trim() || null },
                { withCredentials: true }
            );
            toast.success("Servicio actualizado");
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo actualizar");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        setDeleting(true);
        try {
            await axios.delete(`${API}/admin/procedures/${proc.id}`, {
                withCredentials: true,
            });
            toast.success("Servicio eliminado");
            onDeleted();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo eliminar");
        } finally {
            setDeleting(false);
            setConfirming(false);
        }
    };

    return (
        <Card className="rounded-2xl border-border bg-card p-4" data-testid={`procedure-row-${proc.id}`}>
            <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[220px]">
                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">
                        Nombre del servicio
                    </label>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="h-10 rounded-xl"
                        data-testid={`procedure-name-${proc.id}`}
                    />
                </div>
                <div>
                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">
                        Precio ($)
                    </label>
                    <Input
                        type="number"
                        min={1}
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        className="h-10 w-28 rounded-xl"
                        data-testid={`procedure-price-${proc.id}`}
                    />
                </div>
                <div className="w-full">
                    <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">
                        Descripción corta (visible para el paciente, opcional)
                    </label>
                    <Input
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder='Ej. "Incluye material de curación"'
                        maxLength={200}
                        className="h-10 rounded-xl"
                        data-testid={`procedure-description-${proc.id}`}
                    />
                </div>
                <Button
                    onClick={save}
                    disabled={saving}
                    className="h-10 rounded-xl"
                    data-testid={`procedure-save-${proc.id}`}
                >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                </Button>
                {confirming ? (
                    <div className="flex gap-1.5">
                        <Button
                            variant="destructive"
                            onClick={remove}
                            disabled={deleting}
                            className="h-10 rounded-xl px-3 text-xs"
                            data-testid={`procedure-delete-confirm-${proc.id}`}
                        >
                            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sí, eliminar"}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setConfirming(false)}
                            className="h-10 rounded-xl px-3 text-xs"
                        >
                            No
                        </Button>
                    </div>
                ) : (
                    <Button
                        variant="outline"
                        onClick={() => setConfirming(true)}
                        className="h-10 rounded-xl text-destructive border-destructive/40 hover:bg-destructive/10"
                        data-testid={`procedure-delete-${proc.id}`}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                )}
            </div>
        </Card>
    );
};

const AddProcedureForm = ({ onCreated }) => {
    const [name, setName] = useState("");
    const [price, setPrice] = useState("");
    const [description, setDescription] = useState("");
    const [saving, setSaving] = useState(false);

    const create = async () => {
        const numPrice = parseInt(price, 10);
        if (!name.trim() || name.trim().length < 3) {
            toast.error("El nombre debe tener al menos 3 caracteres");
            return;
        }
        if (!numPrice || numPrice <= 0) {
            toast.error("Ingresa un precio válido");
            return;
        }
        setSaving(true);
        try {
            await axios.post(
                `${API}/admin/procedures`,
                { name: name.trim(), price: numPrice, description: description.trim() || null },
                { withCredentials: true }
            );
            toast.success(`Servicio agregado: ${name.trim()}`);
            setName("");
            setPrice("");
            setDescription("");
            onCreated();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo agregar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 p-4 mb-6" data-testid="add-procedure-form">
            <p className="text-sm font-medium mb-3 flex items-center gap-2">
                <Plus className="h-4 w-4 text-primary" />
                Agregar nuevo servicio
            </p>
            <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[220px]">
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Nombre del servicio"
                        className="h-10 rounded-xl bg-background"
                        data-testid="new-procedure-name"
                    />
                </div>
                <Input
                    type="number"
                    min={1}
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="Precio $"
                    className="h-10 w-28 rounded-xl bg-background"
                    data-testid="new-procedure-price"
                />
                <div className="w-full">
                    <Input
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder='Descripción corta opcional — ej. "Incluye material de curación"'
                        maxLength={200}
                        className="h-10 rounded-xl bg-background"
                        data-testid="new-procedure-description"
                    />
                </div>
                <Button
                    onClick={create}
                    disabled={saving}
                    className="h-10 rounded-xl"
                    data-testid="new-procedure-submit"
                >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
                    Agregar
                </Button>
            </div>
        </Card>
    );
};

export default function AdminProcedures({ onBack }) {
    const [procs, setProcs] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const res = await axios.get(`${API}/procedures`);
            setProcs(res.data || []);
        } catch {
            toast.error("No se pudieron cargar los servicios");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    return (
        <div className="min-h-screen bg-background">
            <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
                <Button
                    variant="outline"
                    onClick={onBack}
                    className="rounded-2xl mb-6"
                    data-testid="procedures-back-btn"
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Volver a citas
                </Button>

                <div className="mb-8">
                    <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2 inline-flex items-center gap-2">
                        <Stethoscope className="h-3.5 w-3.5 text-primary" />
                        Catálogo
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight">
                        Servicios y precios
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        Agrega, edita o elimina los servicios que ofreces. Los cambios aplican
                        de inmediato en la página de reservas. Las citas ya creadas conservan
                        el precio con el que se reservaron.
                    </p>
                </div>

                <AddProcedureForm onCreated={load} />

                {loading ? (
                    <div className="flex items-center justify-center py-16 text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Cargando...
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="procedures-list">
                        {procs.map((p) => (
                            <ProcedureRow key={p.id} proc={p} onSaved={load} onDeleted={load} />
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
